import { Form } from "@inertiajs/react"
import { Loader2 } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { RewardCoverImageField } from "@/components/rewards/reward-cover-image-field"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"

/**
 * RewardForm — shared Inertia `<Form>` body used by the Sheet host
 * inside `Brand/Rewards/Index` plus the standalone `New.tsx` and
 * `Edit.tsx` pages.
 *
 * Crew form rules (TRD K9):
 *   - Inertia `<Form>` from `@inertiajs/react`, render-prop owns
 *     `processing` + `errors`. No `Form` / `FormField` / `FormMessage`
 *     from shadcn. No `react-hook-form`. No `zod`.
 *   - All inputs use `name="reward[*]"` so Rails strong-params can
 *     `params.require(:reward).permit(...)` directly.
 *   - Errors render inline below each input via the `errors`
 *     render-prop; the controller's `serialise_errors` returns
 *     `{ field: ["msg"] }`.
 *
 * `kind` uses a controlled shadcn `Select` paired with a hidden
 * `<input name="reward[kind]">` because shadcn's Select does NOT
 * forward its value to a native form field. The hidden input is the
 * Rails-visible source of truth; the visible Select drives it via the
 * `value` / `onValueChange` props.
 *
 * `is_active` follows the same Switch-vs-form pattern. Default is
 * `true` on the New path (controlled here so the row toggles defy
 * the Browser's empty-checkbox-omitted-from-multipart behaviour).
 */
export interface RewardFormReward {
  id: number | null
  title: string
  description: string
  kind: string | null
  coin_cost: number
  /**
   * OKS-62 — per-reward inventory ceiling.
   *
   *   `null`  → unlimited (the input renders blank, placeholder visible)
   *   `0`     → depleted   (the input renders `0`; brand can restock)
   *   `N > 0` → N remaining (the input renders the numeric value)
   *
   * On submit, the form's blank input posts an empty string; the
   * Rails strong-param coercion in `RewardsController#reward_params`
   * collapses it to NULL = unlimited.
   */
  stock: number | null
  /**
   * OKS-407 — reward lifecycle state (TRD KD-2). One of
   * `available` | `coming_soon` | `locked`. Replaces the OKS-59
   * `is_active` boolean. Drives the redeem gate + card treatment.
   */
  state: string
  /**
   * OKS-438 — resolved cover URL (attached `:thumb` variant → legacy
   * `cover_image_url` string → null) from the server's
   * `cover_image_display_url` (TRD KD-2). Drives the Sheet preview tile.
   * NOT the raw legacy column — the editor no longer edits a URL string.
   */
  cover_image_url: string | null
  /**
   * OKS-438 — true when an Active Storage attachment exists. Gates the
   * Remove affordance (legacy-URL-only rows can't be purged). New
   * rewards default to `false`.
   */
  has_cover_image: boolean
}

interface RewardFormProps {
  reward: RewardFormReward
  kinds: string[]
  action: string
  method: "post" | "patch"
  /** Optional secondary action — e.g. the Cancel button inside a Sheet. */
  onCancel?: () => void
  testId?: string
}

export function RewardForm({
  reward,
  kinds,
  action,
  method,
  onCancel,
  testId = "reward-form",
}: RewardFormProps) {
  const { t } = useTranslation()

  return (
    <Form
      action={action}
      method={method}
      options={{ preserveScroll: true }}
      className="flex flex-col gap-6"
      data-testid={testId}
    >
      {({ processing, errors }) => (
        <RewardFormBody
          reward={reward}
          kinds={kinds}
          processing={processing}
          errors={errors as Record<string, string | string[]>}
          onCancel={onCancel}
          t={t}
        />
      )}
    </Form>
  )
}

interface RewardFormBodyProps {
  reward: RewardFormReward
  kinds: string[]
  processing: boolean
  errors: Record<string, string | string[]>
  onCancel?: () => void
  t: (key: string) => string
}

function RewardFormBody({
  reward,
  kinds,
  processing,
  errors,
  onCancel,
  t,
}: RewardFormBodyProps) {
  return (
    <>
      <div className="grid gap-2">
        <Label htmlFor="reward-title">
          {t("rewards.form.title_label")}
          <RequiredMark />
        </Label>
        <Input
          id="reward-title"
          name="reward[title]"
          type="text"
          defaultValue={reward.title}
          maxLength={120}
          aria-required="true"
          aria-invalid={Boolean(errors.title)}
          aria-describedby={errors.title ? "reward-title-error" : undefined}
        />
        <FieldError
          id="reward-title-error"
          error={errors.title}
          testId="reward-form-error-title"
        />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="reward-description">
          {t("rewards.form.description_label")}
          <RequiredMark />
        </Label>
        <Textarea
          id="reward-description"
          name="reward[description]"
          rows={4}
          defaultValue={reward.description}
          maxLength={2000}
          aria-required="true"
          aria-invalid={Boolean(errors.description)}
          aria-describedby={
            errors.description ? "reward-description-error" : undefined
          }
        />
        <FieldError
          id="reward-description-error"
          error={errors.description}
          testId="reward-form-error-description"
        />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="reward-kind">{t("rewards.form.kind_label")}</Label>
        <KindSelect
          kinds={kinds}
          defaultKind={reward.kind}
          placeholder={t("rewards.form.kind_placeholder")}
          ariaInvalid={Boolean(errors.kind)}
          t={t}
        />
        <FieldError id="reward-kind-error" error={errors.kind} />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="reward-coin-cost">
          {t("rewards.form.coin_cost_label")}
          <RequiredMark />
        </Label>
        <Input
          id="reward-coin-cost"
          name="reward[coin_cost]"
          type="number"
          inputMode="numeric"
          min={0}
          step={1}
          defaultValue={reward.coin_cost}
          aria-required="true"
          aria-invalid={Boolean(errors.coin_cost)}
          aria-describedby={
            errors.coin_cost ? "reward-coin-cost-error" : undefined
          }
        />
        <FieldError
          id="reward-coin-cost-error"
          error={errors.coin_cost}
          testId="reward-form-error-coin-cost"
        />
      </div>

      <StockField stock={reward.stock} error={errors.stock} t={t} />

      <RewardCoverImageField
        currentUrl={reward.cover_image_url}
        hasCoverImage={reward.has_cover_image}
        kind={reward.kind}
        error={errors.cover_image}
      />

      <StateSelect defaultState={reward.state} t={t} />

      <div className="flex items-center justify-end gap-2">
        {onCancel ? (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            data-testid="reward-cancel"
          >
            {t("rewards.form.cancel")}
          </Button>
        ) : null}
        <Button type="submit" disabled={processing}>
          {processing ? (
            <Loader2 className="mr-2 size-4 animate-spin" aria-hidden="true" />
          ) : null}
          {t("rewards.form.submit")}
        </Button>
      </div>
    </>
  )
}

/**
 * Controlled `Select` + hidden `<input>` pairing. shadcn's Select
 * doesn't surface its value via a native form field; the hidden input
 * carries `name="reward[kind]"` so Rails sees the choice.
 *
 * Initialised from `defaultKind` (the persisted value on Edit or
 * `null` on New). The local state stays in sync with the trigger so
 * an `errors.kind` re-paint preserves the user's selection.
 */
interface KindSelectProps {
  kinds: string[]
  defaultKind: string | null
  placeholder: string
  ariaInvalid: boolean
  t: (key: string) => string
}

function KindSelect({
  kinds,
  defaultKind,
  placeholder,
  ariaInvalid,
  t,
}: KindSelectProps) {
  const [value, setValue] = useState<string>(defaultKind ?? "")

  return (
    <>
      <input type="hidden" name="reward[kind]" value={value} />
      <Select
        items={kinds.map((kind) => ({
          value: kind,
          label: t(`rewards.kind.${kind}`),
        }))}
        value={value}
        onValueChange={(next) => setValue(next ?? "")}
      >
        <SelectTrigger
          id="reward-kind"
          aria-invalid={ariaInvalid}
          aria-describedby={ariaInvalid ? "reward-kind-error" : undefined}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {kinds.map((kind) => (
            <SelectItem key={kind} value={kind}>
              {t(`rewards.kind.${kind}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </>
  )
}

/**
 * OKS-407 — State Select + hidden input pair (replaces the OKS-59
 * Active Switch; TRD KD-3). shadcn's Select doesn't forward its value
 * to a native form field, so the hidden `<input name="reward[state]">`
 * is the Rails-visible source of truth. Default is `available` on New.
 */
interface StateSelectProps {
  defaultState: string
  t: (key: string) => string
}

const REWARD_FORM_STATES = ["available", "coming_soon", "locked"] as const

function StateSelect({ defaultState, t }: StateSelectProps) {
  const [value, setValue] = useState<string>(defaultState || "available")

  return (
    <div className="grid gap-2">
      <input type="hidden" name="reward[state]" value={value} />
      <Label htmlFor="reward-state">{t("rewards.form.state_label")}</Label>
      <Select
        items={REWARD_FORM_STATES.map((option) => ({
          value: option,
          label: t(`rewards.state.${option}`),
        }))}
        value={value}
        onValueChange={(next) => setValue(next ?? "")}
      >
        <SelectTrigger id="reward-state">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {REWARD_FORM_STATES.map((option) => (
            <SelectItem key={option} value={option}>
              {t(`rewards.state.${option}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-muted-foreground text-sm">
        {t("rewards.form.state_help")}
      </p>
    </div>
  )
}

/**
 * OKS-62 — Stock field. Plain `<Input type="number">`, NOT required.
 *
 * UX contract (UX Spec §3 + PRD OQ-2):
 *   - Empty input = NULL stock = unlimited (the default; the placeholder
 *     reinforces this — "Leave empty for unlimited").
 *   - Numeric input = finite ceiling (≥ 0; the model validator catches
 *     negatives + decimals + non-numerics).
 *   - On Edit, pre-fills with the current numeric value (or empty when
 *     `stock IS NULL`) — `defaultValue={stock ?? ""}` is the React-safe
 *     way to express "no value" for a controlled-ish input.
 *
 * Why no shadcn `Form` / `FormField` / `FormMessage`:
 *   - Crew hard rule (CLAUDE.md). Same posture as every other field in
 *     this form. Error renders via the `FieldError` helper below.
 *
 * Why a hint paragraph (`text-muted-foreground text-xs`):
 *   - The placeholder is gone the moment the user types; the persistent
 *     hint reinforces "blank = unlimited" so a brand admin doesn't
 *     mistype `0` thinking that means "no cap". UX Spec §3.
 *
 * Accessibility:
 *   - `aria-describedby` points to BOTH the hint paragraph AND the error
 *     region (when present). Screen readers announce both.
 */
interface StockFieldProps {
  stock: number | null
  error: string | string[] | undefined
  t: (key: string) => string
}

function StockField({ stock, error, t }: StockFieldProps) {
  const hasError = Boolean(error)
  const describedBy = hasError
    ? "reward-stock-hint reward-stock-error"
    : "reward-stock-hint"

  return (
    <div className="grid gap-2">
      <Label htmlFor="reward-stock">{t("rewards.form.stock_label")}</Label>
      <Input
        id="reward-stock"
        name="reward[stock]"
        type="number"
        inputMode="numeric"
        min={0}
        step={1}
        defaultValue={stock ?? ""}
        placeholder={t("rewards.form.stock_placeholder")}
        aria-invalid={hasError}
        aria-describedby={describedBy}
      />
      <p id="reward-stock-hint" className="text-muted-foreground text-xs">
        {t("rewards.form.stock_hint")}
      </p>
      <FieldError id="reward-stock-error" error={error} />
    </div>
  )
}

interface FieldErrorProps {
  id: string
  error: string | string[] | undefined
  /** Optional stable QA hook (OKS-661) so E2E can key the error slot. */
  testId?: string
}

function FieldError({ id, error, testId }: FieldErrorProps) {
  if (!error) return null

  const message = Array.isArray(error) ? error[0] : error
  if (!message) return null

  return (
    <p
      id={id}
      role="alert"
      data-testid={testId}
      className="text-destructive mt-1 text-sm"
    >
      {message}
    </p>
  )
}

/**
 * OKS-661 — visual required marker. Required fields drop the native
 * HTML5 `required` attribute (so blank submits reach the server and
 * render a localized inline error rather than the browser's
 * untranslated native bubble); required-ness is instead signaled by
 * this `*` marker + `aria-required="true"` on the control.
 */
function RequiredMark() {
  return (
    <span className="text-destructive ml-0.5" aria-hidden="true">
      *
    </span>
  )
}
